import { create } from 'zustand';
import {
	RecognizedLabelsStructureType,
	FileType,
	StringRecognizedLabel,
	TableCellType,
	TableRecognizedLabel,
} from 'entities/Files/types';
import { ModelsType } from 'entities/CustomModel/types';

type FileEditorStore = {
	fileDetails: FileType | null;
	customModelFileDetails: ModelsType | null;
};

type FileEditorActions = {
	setFileDetailsV2: (details: FileType) => void;
	setCustomModelFileDetailsV2: (details: ModelsType) => void;
	updateRecognizedLabels: (recognizedLabels: RecognizedLabelsStructureType[]) => void;
	updateTableCell: (
		tableIndex: number,
		rowIndex: number,
		columnIndex: number,
		patch: Partial<TableCellType>,
	) => void;
	updateLabelContent: (labelIndex: number, content: string) => void;
	updateLabel: (labelIndex: number, patch: Partial<StringRecognizedLabel>) => void;
	addTableRow: (tableIndex: number) => void;
	addTable: (tableName: string, columns: string[]) => void;
	reset: () => void;
};

const initialState: FileEditorStore = {
	fileDetails: null,
	customModelFileDetails: null,
};

const fileEditorStore = create<FileEditorStore & FileEditorActions>()((set) => ({
	...initialState,
	setFileDetailsV2: (fileDetails) => set(() => ({ fileDetails })),
	setCustomModelFileDetailsV2: (customModelFileDetails) => set(() => ({ customModelFileDetails })),
	updateRecognizedLabels: (recognizedLabels) =>
		set(({ fileDetails }) => (fileDetails ? { fileDetails: { ...fileDetails, recognizedLabels } } : {})),
	updateTableCell: (tableIndex, rowIndex, columnIndex, patch) =>
		set(({ fileDetails }) => {
			if (!fileDetails) return {};
			const recognizedLabels = fileDetails.recognizedLabels.map((label, index) => {
				if (index !== tableIndex || label.type !== 'table') return label;
				const cellIndex = label.cells.findIndex(
					(cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex,
				);
				const cells = [...label.cells];
				if (cellIndex >= 0) {
					cells[cellIndex] = { ...cells[cellIndex], ...patch };
				} else {
					cells.push({ rowIndex, columnIndex, content: '', ...patch });
				}
				return { ...label, cells };
			});
			return { fileDetails: { ...fileDetails, recognizedLabels } };
		}),
	updateLabelContent: (labelIndex, content) =>
		set(({ fileDetails }) => {
			if (!fileDetails) return {};
			const recognizedLabels = fileDetails.recognizedLabels.map((label, index) => {
				if (index !== labelIndex || label.type !== 'string') return label;
				return { ...label, content };
			});
			return { fileDetails: { ...fileDetails, recognizedLabels } };
		}),
	updateLabel: (labelIndex, patch) =>
		set(({ fileDetails }) => {
			if (!fileDetails) return {};
			const recognizedLabels = fileDetails.recognizedLabels.map((label, index) => {
				if (index !== labelIndex || label.type !== 'string') return label;
				return { ...label, ...patch };
			});
			return { fileDetails: { ...fileDetails, recognizedLabels } };
		}),
	addTableRow: (tableIndex) =>
		set(({ fileDetails }) => {
			if (!fileDetails) return {};
			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) => {
				if (index !== tableIndex || entry.type !== 'table') return entry;
				const columnCount = entry.columnsHeaders.length;
				const nextRowIndex = entry.cells.length
					? Math.max(...entry.cells.map((cell) => cell.rowIndex)) + 1
					: 0;
				const newCells: TableCellType[] = Array.from({ length: columnCount }, (_, columnIndex) => ({
					content: '',
					rowIndex: nextRowIndex,
					columnIndex,
				}));
				return { ...entry, cells: [...entry.cells, ...newCells] };
			});
			return { fileDetails: { ...fileDetails, recognizedLabels } };
		}),
	addTable: (tableName, columns) =>
		set(({ fileDetails }) => {
			if (!fileDetails) return {};
			const newTable: TableRecognizedLabel = {
				type: 'table',
				label: tableName,
				columnsHeaders: columns.map((columnName) => ({ columnName })),
				cells: [],
			};
			return {
				fileDetails: {
					...fileDetails,
					recognizedLabels: [...(fileDetails.recognizedLabels || []), newTable],
				},
			};
		}),
	reset: () => set(() => ({ ...initialState })),
}));

export const useFileEditorStore = () => fileEditorStore((state) => state);
